import { getGapOfferMarkerInformations } from 'api/gapOffer';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
  GAP_MAP_ZOOM_LEVEL,
} from 'constants/map';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMarkerInfo, setModal } from 'redux/store/mapReducer';
import { RootState } from 'redux/store/rootReducer';
import styled from 'styled-components';
import { GapFilter, GapOfferMapFilter, GapOfferMarker } from 'types/gapOffer';
import { getReactNativeMessage } from 'utils/ReactNativeMessage';
import { TMapOptions } from '../../types/naverMap';
import GapMarker from './GapMarker';

const { naver } = window;
const GapMap: FunctionComponent<TMapOptions> = () => {
  const map = useRef<naver.maps.Map>();
  const dispatch = useDispatch();
  const { bldInfo } = useSelector((state: RootState) => state.mapStore);
  const [markerArray, setMarkerArray] = useState<GapOfferMarker[]>([]);
  const [selectedBldNm, setSelectedBldNm] = useState('');

  const [filteredData, setFilteredData] = useState<GapFilter>({
    gapcost: [0, 12],
    saleprice: [0, 25],
    type: [0, 80],
    date: [0, 8],
  });

  useEffect(() => {
    if (map.current) moveToBuildingPosition(map.current);
  }, [bldInfo]);

  useEffect(() => {
    getReactNativeMessage({ handleFilteredData });

    drawMap();
  }, []);

  useEffect(() => {
    if (!map.current) return () => {};

    drawMarker(map.current, filteredData);

    const idleListener = map.current.addListener('idle', () => {
      if (map.current) drawMarker(map.current, filteredData);
    });

    const clickListener = map.current.addListener('click', () => {
      dispatch(setModal(false));
      dispatch(setMarkerInfo({}));
    });

    return () => {
      if (map.current) {
        map.current.removeListener(idleListener);
        map.current.removeListener(clickListener);
      }
    };
  }, [filteredData]);

  function handleClickMarker(selectedBldNm: string) {
    setSelectedBldNm(selectedBldNm);
  }

  async function drawMap() {
    const { zoomLevel, centerX, centerY } = getSavedPositionFromLocalStorage();
    const mapOptions: naver.maps.MapOptions = {
      zoom: zoomLevel || DEFAULT_ZOOM,
      center: new naver.maps.LatLng(
        centerY || DEFAULT_LATITUDE,
        centerX || DEFAULT_LONGITUDE,
      ),
    };
    map.current = new naver.maps.Map('map', mapOptions);

    map.current?.morph(
      new naver.maps.LatLng(
        centerY || DEFAULT_LATITUDE,
        centerX || DEFAULT_LONGITUDE,
      ),
      zoomLevel || DEFAULT_ZOOM,
    );
  }

  const getSavedPositionFromLocalStorage = () => {
    const zoomLevel = Number(window.localStorage.getItem('gapZoom'));
    const centerY = Number(window.localStorage.getItem('gapCenterY'));
    const centerX = Number(window.localStorage.getItem('gapCenterX'));

    return { zoomLevel, centerY, centerX };
  };

  const saveCurrentPositionToLocalStorage = (mapObject: naver.maps.Map) => {
    const { x, y } = mapObject.getCenter();
    const zoom = mapObject.getZoom();

    window.localStorage.setItem('gapZoom', `${zoom}`);
    window.localStorage.setItem('gapCenterY', `${y}`);
    window.localStorage.setItem('gapCenterX', `${x}`);
  };

  const drawMarker = async (map: naver.maps.Map, filters: GapFilter) => {
    saveCurrentPositionToLocalStorage(map);

    const zoom = map.getZoom();
    const { x: longitude, y: latitude } = map.getCenter();
    const bounds = map.getBounds();
    const { x: west, y: south } = bounds.getMin();
    const { x: east, y: north } = bounds.getMax();
    const mapdata: GapOfferMapFilter = {
      zoom,
      latitude,
      longitude,
      west,
      north,
      east,
      south,
      gapMin: filters.gapcost[0],
      gapMax: filters.gapcost[1],
      priceMin: filters.saleprice[0],
      priceMax: filters.saleprice[1],
      areaMin: filters.type[0],
      areaMax: filters.type[1],
      dateMin: filters.date[0],
      dateMax: filters.date[1],
    };
    const { data, error } = await getGapOfferMarkerInformations(mapdata);

    if (error || !data) {
      return;
    }

    setMarkerArray(data);

    if (map.getZoom() < GAP_MAP_ZOOM_LEVEL.complex) {
      dispatch(setMarkerInfo({}));
      dispatch(setModal(false));
    }
  };

  const moveToBuildingPosition = (map: naver.maps.Map) => {
    if (bldInfo) {
      const newCenter = new naver.maps.LatLng(
        bldInfo?.complexLatCenter,
        bldInfo?.complexLonCenter,
      );

      map.morph(newCenter, 17);
    }
  };

  const handleFilteredData = (item: GapFilter) => {
    setFilteredData(item);
  };

  return (
    <>
      <NaverMap id="map">
        {!markerArray.length && (
          <PopUp>
            <PopupLabel>검색 결과가 없습니다.</PopupLabel>
            <PopupLabel>필터를 변경하거나</PopupLabel>
            <PopupLabel>지도를 이동시켜 주세요.</PopupLabel>
          </PopUp>
        )}
        {markerArray?.map((markerInfo) => {
          return (
            <GapMarker
              key={
                markerInfo.bldNm +
                markerInfo.complexId +
                markerInfo.districtName
              }
              map={map.current}
              markerInfo={markerInfo}
              onClick={handleClickMarker}
              selectedBldNm={selectedBldNm}
            />
          );
        })}
      </NaverMap>
    </>
  );
};

export default GapMap;

const PopUp = styled.div`
  border-radius: 8px;
  z-index: 1;
  top: 20%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  padding: 16px;
  opacity: 0.5;
  position: absolute;
  background-color: black;
`;

const PopupLabel = styled.div`
  font-family: Pretendard;
  font-style: normal;
  font-weight: bold;
  font-size: 13px;
  line-height: 20px;
  vertical-align: middle;
  text-align: center;
  color: white;
`;

const NaverMap = styled.div`
  width: 100%;
  height: 100%;
`;

GapMap.defaultProps = {
  center: { x: 33.3572421, y: 126.5322317 },
  mapTypeId: 'NORMAL',
};
